import type { InquiryAction, InquiryStatus } from 'src/models/inquiry.model';

export type InquiryStatusTone =
  | 'needs-response'
  | 'preapproved'
  | 'booked'
  | 'declined'
  | 'expired'
  | 'neutral';

export type InquiryPrimaryAction = 'approve' | 'preapprove';

/** The fields a presented status and its detail line are derived from. */
export interface InquiryLifecycle {
  status: InquiryStatus | string;
  platform: string;
  available_actions: InquiryAction[];
  expires_at?: string | null;
  check_in_date: string | null;
}

/**
 * The platforms the backend offers host actions on (gotidy's
 * `Inquiry::CUSTOMER_ACTIONS`). Anywhere else `available_actions` is always
 * empty, so an empty list says nothing about whether the guest is still waiting.
 */
const PLATFORMS_WITH_HOST_ACTIONS = new Set(['airbnb', 'tidy']);

/** Resolve the backend-advertised primary action without conflating its flows. */
export function inquiryPrimaryAction(
  actions: InquiryAction[]
): InquiryPrimaryAction | null {
  if (actions.includes('approve')) {
    return 'approve';
  }
  if (actions.includes('preapprove')) {
    return 'preapprove';
  }
  return null;
}

/**
 * The lifecycle status to PRESENT for an inquiry — the backend status, except
 * that an inquiry nobody can act on any more reads as expired. Two states lag
 * reality in different ways:
 *
 * 'preapproved' — the backend NEVER writes this one back. `preapprove` sets
 * status + `expires_at` and no sweep revisits it, on purpose: the guest's stub
 * vanishes from the channel the moment the host pre-approves, and a booking may
 * still land afterwards via reconciliation (which would make it 'approved').
 * So the 24h lapse is ours to present — otherwise a months-dead pre-approval
 * still advertises a live booking window.
 *
 * 'pending' — the backend's sweep flips it to 'expired' asynchronously, so a
 * dead inquiry can sit at 'pending' for a while (indefinitely on sandbox
 * environments without the sweep). Two signals are reliable before it runs:
 * on a platform with host actions, `available_actions` empties the moment an
 * inquiry stops being actionable, and a past `expires_at` on a still-pending
 * row means the same thing. Without this, a dead row would wear the
 * alert-colored "Needs response" badge with no buttons.
 *
 * On the other channels (Vrbo, Booking.com) the host answers on the channel,
 * no actions are ever offered and no deadline is known, so a pending row stays
 * pending until its check-in day has passed.
 *
 * Time-dependent by nature: a row can cross into 'expired' with no new data,
 * so anything holding a computed status has to recompute it on a timer.
 *
 * Pure: `now` is injectable for tests.
 */
export function effectiveInquiryStatus(
  inquiry: InquiryLifecycle,
  now: number = Date.now()
): InquiryStatus | string {
  if (inquiry.status === 'preapproved') {
    return hasLapsed(inquiry.expires_at, now) ? 'expired' : inquiry.status;
  }
  if (inquiry.status !== 'pending') {
    return inquiry.status;
  }
  if (hasLapsed(inquiry.expires_at, now)) {
    return 'expired';
  }
  if (PLATFORMS_WITH_HOST_ACTIONS.has(inquiry.platform)) {
    return inquiry.available_actions.length === 0 ? 'expired' : inquiry.status;
  }
  return checkInHasPassed(inquiry.check_in_date, now) ? 'expired' : inquiry.status;
}

/**
 * Whether a KNOWN deadline has passed. A missing or unparseable `expires_at` is
 * not evidence that anything lapsed, so it never expires a row on its own.
 */
function hasLapsed(expiresAt: string | null | undefined, now: number): boolean {
  const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
  return Number.isFinite(expiry) && expiry <= now;
}

/**
 * Whether the stay's check-in day (YYYY-MM-DD) is before today. A missing or
 * unparseable date is not evidence that anything lapsed.
 */
function checkInHasPassed(checkInDate: string | null, now: number): boolean {
  if (!checkInDate || !/^\d{4}-\d{2}-\d{2}/.test(checkInDate)) {
    return false;
  }
  const today = new Date(now);
  const todayKey = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');
  return checkInDate.slice(0, 10) < todayKey;
}

/**
 * User-facing inquiry lifecycle labels shared by the inquiry list and detail.
 * Unknown statuses are humanized so future direct-booking states remain legible
 * until they receive a dedicated label and tone.
 */
export function inquiryStatusLabel(status: InquiryStatus | string): string {
  switch (status) {
    case 'pending':
      return 'Needs response';
    case 'preapproved':
      return 'Pre-approved';
    case 'approved':
      return 'Booked';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
    case 'responded':
      // Distinct from 'approved' on purpose: a reply resolves a question, it
      // does not grant the guest a booking window.
      return 'Replied';
    default:
      return humanizeStatus(status);
  }
}

export function inquiryStatusTone(status: InquiryStatus | string): InquiryStatusTone {
  switch (status) {
    case 'pending':
      return 'needs-response';
    case 'preapproved':
      return 'preapproved';
    case 'approved':
      return 'booked';
    case 'declined':
      return 'declined';
    case 'expired':
      return 'expired';
    default:
      return 'neutral';
  }
}

/**
 * Sort rank for the table's Status column — unresolved first, dead last, so an
 * ascending sort brings the rows still waiting on the host to the top. Sorting
 * on the label instead would file "Booked" above "Needs response".
 *
 * Unknown statuses sort after the known ones rather than interleaving on a
 * label they don't have a lifecycle position for.
 */
export function inquiryStatusOrder(status: InquiryStatus | string): number {
  switch (status) {
    case 'pending':
      return 0;
    case 'preapproved':
      return 1;
    case 'approved':
      return 2;
    case 'responded':
      return 3;
    case 'declined':
      return 4;
    case 'expired':
      return 5;
    default:
      return 6;
  }
}

/** Everything a lifecycle badge renders, resolved together. */
export interface InquiryStatusPresentation {
  status: InquiryStatus | string;
  label: string;
  detail: string | null;
  tone: InquiryStatusTone;
  order: number;
}

/**
 * The whole badge, resolved from a SINGLE reading of the clock.
 *
 * The parts are time-dependent and change at the same instant, so resolving
 * them one call at a time invites exactly the drift this exists to prevent: a
 * status that lapses between two `Date.now()` calls renders "Expired" over a
 * "Guest can book · 1h left" countdown. Callers that display a badge should
 * take it from here rather than assembling one.
 *
 * `order` rides along for the same reason. It is what the Status column sorts
 * on, and it moves at the same instant the label does — resolved separately, a
 * row that lapses into Expired would keep sorting where Pre-approved used to
 * sit until the next reload.
 */
export function inquiryStatusPresentation(
  inquiry: InquiryLifecycle,
  now: number = Date.now()
): InquiryStatusPresentation {
  const status = effectiveInquiryStatus(inquiry, now);
  return {
    status,
    label: inquiryStatusLabel(status),
    detail: inquiryStatusDetail(inquiry, now),
    tone: inquiryStatusTone(status),
    order: inquiryStatusOrder(status),
  };
}

/**
 * The line under the status badge: what the host can still expect from this
 * inquiry, or what became of it. Derived from the same lifecycle fields as the
 * badge itself so the two can never describe different moments in time.
 */
export function inquiryStatusDetail(
  inquiry: InquiryLifecycle,
  now: number = Date.now()
): string | null {
  switch (effectiveInquiryStatus(inquiry, now)) {
    case 'preapproved':
      return preapprovalStatusDetail(inquiry.expires_at ?? undefined, now);
    case 'expired':
      return expiredInquiryDetail(inquiry.status);
    default:
      return null;
  }
}

/**
 * Which of the two ways an inquiry expired: the host pre-approved and the guest
 * let the window lapse, or nobody ever replied. Both wear the same muted
 * Expired badge — the page stays scannable — and this line carries the
 * difference, since only one of them is a missed opportunity.
 *
 * Reads the RAW backend status, not the effective one: 'preapproved' is only
 * ever written by the host's own pre-approve action and nothing overwrites it
 * afterwards, so it survives as the record that the host did respond.
 *
 * That survival is the whole mechanism. Should the backend ever gain a sweep
 * that flips lapsed pre-approvals to 'expired', the record is gone and every
 * one of them would read "No response sent" — the sweep would have to preserve
 * the distinction (a timestamp, a reason) for this to keep working.
 */
export function expiredInquiryDetail(status: InquiryStatus | string): string {
  return status === 'preapproved' ? "Pre-approved · guest didn't book" : 'No response sent';
}

/**
 * Remaining Airbnb booking window shown beneath the Pre-approved badge.
 *
 * A lapsed window resolves to Expired before this is reached, so the
 * non-positive branch below is a floor for direct callers, not a state the
 * badge ever displays — it used to be what a stale pre-approval froze on.
 */
export function preapprovalStatusDetail(
  expiresAt?: string,
  now: number = Date.now()
): string {
  if (!expiresAt) {
    return 'Guest can book within 24h';
  }

  const expiry = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiry)) {
    return 'Guest can book within 24h';
  }

  const remainingMs = expiry - now;
  if (remainingMs <= 0) {
    return 'Guest booking window ending soon';
  }

  const hours = Math.max(1, Math.ceil(remainingMs / 3_600_000));
  if (hours <= 24) {
    return `Guest can book · ${hours}h left`;
  }

  const days = Math.ceil(hours / 24);
  return `Guest can book · ${days}d left`;
}

function humanizeStatus(status: string): string {
  const label = status.trim().replace(/[_-]+/g, ' ');
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : 'Inquiry';
}
